import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { absoluteUrl } from "@/lib/site-config";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import LegalTocMobile from "@/components/legal/LegalTocAside";
import LegalTocFixed from "@/components/legal/LegalTocFixed";

const purple = "#4A3A8A";
const lavender = "#f0eeff";
const textBody = "#3a3555";

function SectionShell({ id, num, title, children, className = "" }) {
  return (
    <section
      id={id}
      className={`scroll-mt-28 rounded-2xl border border-violet-light/80 bg-white p-6 shadow-sm sm:p-8 ${className}`}
    >
      <div className="mb-5 flex items-start gap-4 border-b border-violet-light/60 pb-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-fuerte text-sm font-bold text-white shadow-[0_4px_14px_-6px_rgba(74,58,138,0.5)]">
          {num}
        </div>
        <h2 className="pt-0.5 font-display text-lg font-bold leading-snug tracking-tight text-ink sm:text-[1.25rem]">
          {title}
        </h2>
      </div>
      <div className="text-[14.5px] leading-[1.78]" style={{ color: textBody }}>
        {children}
      </div>
    </section>
  );
}

function LegalBadge({ kind, children }) {
  const map = {
    contract: "bg-[#e8f5e9] text-[#2e7d32]",
    consent: "bg-[#fff8e1] text-[#f57f17]",
    legit: "bg-[#e3f2fd] text-[#1565c0]",
    legal: "bg-[#fce4ec] text-[#b71c1c]",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${map[kind]}`}>
      {children}
    </span>
  );
}

const tocItems = [
  { href: "#s1", n: "1", label: "Responsable del tratamiento" },
  { href: "#s2", n: "2", label: "Datos que recogemos" },
  { href: "#s3", n: "3", label: "Finalidad y base legal" },
  { href: "#s4", n: "4", label: "Datos especialmente sensibles" },
  { href: "#s5", n: "5", label: "Conservación de datos" },
  { href: "#s6", n: "6", label: "Encargados y terceros" },
  { href: "#s7", n: "7", label: "Transferencias internacionales" },
  { href: "#s8", n: "8", label: "Tus derechos" },
  { href: "#s9", n: "9", label: "Seguridad" },
  { href: "#s10", n: "10", label: "Menores de edad" },
  { href: "#s11", n: "11", label: "Cambios en esta política" },
  { href: "#s12", n: "12", label: "Contacto" },
];

const purposeRows = [
  { purpose: "Prestación del servicio SaaS contratado", legal: "contract", badgeText: "Ejecución de contrato", who: "Administrador" },
  { purpose: "Gestión de la suscripción y facturación", legal: "contract", badgeText: "Ejecución de contrato", who: "Administrador" },
  { purpose: "Gestión de miembros de la congregación", legal: "consent", badgeText: "Consentimiento expreso", who: "Miembros" },
  { purpose: "Comunicaciones sobre el servicio", legal: "legit", badgeText: "Interés legítimo", who: "Administrador" },
  { purpose: "Soporte técnico y atención al cliente", legal: "legit", badgeText: "Interés legítimo", who: "Administrador" },
  { purpose: "Mejora del producto y análisis de uso", legal: "consent", badgeText: "Consentimiento", who: "Administrador" },
  { purpose: "Cumplimiento de obligaciones fiscales", legal: "legal", badgeText: "Obligación legal", who: "Administrador" },
];

const conservationRows = [
  { tipo: "Datos de cuenta del administrador", periodo: "Vigencia del contrato + 5 años" },
  { tipo: "Datos de facturación y pagos", periodo: "5 años (Ley General Tributaria)" },
  { tipo: "Datos de miembros de la congregación", periodo: "Mientras la iglesia tenga suscripción activa" },
  { tipo: "Copias de seguridad", periodo: "Máximo 90 días tras eliminación" },
  { tipo: "Registros de acceso y seguridad", periodo: "12 meses" },
  { tipo: "Datos analíticos anonimizados", periodo: "26 meses" },
];

const dataCards = [
  { label: "Responsable", value: "Osmel Enamorado" },
  { label: "Actividad", value: "Software de gestión eclesiástica (SaaS)" },
  { label: "Sitio web", value: "ekklesiaonline.app" },
  { label: "Correo de contacto", value: "privacidad@ekklesiaonline.app" },
  { label: "País", value: "España" },
  { label: "Marco normativo", value: "RGPD (UE) 2016/679 · LOPDGDD" },
];

const rightsCards = [
  { icon: "👁️", title: "Acceso", desc: "Conoce qué datos tuyos tratamos y obtén una copia." },
  { icon: "✏️", title: "Rectificación", desc: "Corrige datos inexactos o incompletos." },
  { icon: "🗑️", title: "Supresión", desc: "Solicita la eliminación («derecho al olvido»)." },
  { icon: "🚫", title: "Oposición", desc: "Oponte al tratamiento basado en interés legítimo." },
  { icon: "⏸️", title: "Limitación", desc: "Restringe el tratamiento en determinadas circunstancias." },
  { icon: "📦", title: "Portabilidad", desc: "Recibe tus datos en formato estructurado y legible." },
  { icon: "↩️", title: "Retirar consentimiento", desc: "En cualquier momento, sin efecto retroactivo." },
  {
    icon: "⚖️",
    title: "Reclamación ante la AEPD",
    desc: (
      <>
        Presenta queja en{" "}
        <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-fuerte hover:underline">
          aepd.es
        </a>
        .
      </>
    ),
  },
];

function BulletList({ items }) {
  return (
    <ul className="my-3 list-none space-y-2">
      {items.map((t, i) => (
        <li
          key={i}
          className="relative pl-5 text-[14.5px] leading-relaxed before:absolute before:left-0 before:top-[0.62rem] before:h-1.5 before:w-1.5 before:rounded-full before:bg-gold"
          style={{ color: textBody }}
        >
          {t}
        </li>
      ))}
    </ul>
  );
}

function InfoBox({ accent = false, children }) {
  return (
    <div
      className="my-4 rounded-xl border px-5 py-4 text-[13.5px] leading-relaxed"
      style={{
        background: accent ? "rgba(201,162,39,0.07)" : lavender,
        borderColor: accent ? "rgba(201,162,39,0.35)" : "#e4dffe",
        borderLeft: `4px solid ${accent ? "#c9a227" : purple}`,
        color: accent ? "#5a4200" : textBody,
      }}
    >
      {children}
    </div>
  );
}

export default function PrivacyPolicyPage() {
  const sentinelRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#f4f3f9] text-ink">
      <Seo
        title="Política de privacidad"
        description="Política de privacidad de Ekklesia: responsable del tratamiento, datos recogidos, bases legales (RGPD/LOPDGDD), conservación, derechos ARCO+ y contacto."
        path="/privacidad"
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Política de privacidad | Ekklesia",
          description:
            "Información sobre el tratamiento de datos personales en el software de gestión eclesiástica Ekklesia.",
          url: absoluteUrl("/privacidad"),
          isPartOf: { "@type": "WebSite", name: "Ekklesia", url: absoluteUrl("/") },
        }}
      />
      <Header />

      <main className="mx-auto max-w-7xl px-4 pb-28 pt-24 sm:px-6 sm:pt-28 md:px-10 md:pt-32 lg:px-12">
        {/* Wrapper: reserva espacio derecho para el TOC fijo en lg+ */}
        <div className="lg:pr-[calc(16rem+2.5rem)]">

        {/* ── Page header ────────────────────────────────────── */}
        <header>
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-gold">Legal</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-[2.2rem] lg:leading-tight">
            Política de Privacidad
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-2">
            Cómo recogemos, tratamos y protegemos los datos personales de los usuarios y las iglesias que confían en
            Ekklesia.
          </p>
          <div className="mt-7 grid gap-3 sm:grid-cols-3 sm:gap-4">
            {[
              { label: "Actualización", value: "Enero 2025" },
              { label: "Responsable", value: "Osmel Enamorado" },
              { label: "Marco legal", value: "RGPD / LOPDGDD" },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-violet-light/80 bg-white/90 px-4 py-3 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-wide text-ink-4">{c.label}</p>
                <p className="mt-1 font-semibold text-ink">{c.value}</p>
              </div>
            ))}
          </div>
        </header>

        {/* Mobile TOC */}
        <LegalTocMobile ariaLabel="Índice de la política de privacidad" tocItems={tocItems} />

        {/* ── Article ── */}
        <div className="mt-10 space-y-6">
            {/* s1 */}
            <SectionShell id="s1" num="1" title="Responsable del tratamiento">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {dataCards.map((c) => (
                  <div key={c.label} className="rounded-xl border border-[#e4dffe] px-4 py-3.5" style={{ background: lavender }}>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-violet-medio">{c.label}</p>
                    <p className="mt-1.5 text-sm font-medium text-violet-oscuro">{c.value}</p>
                  </div>
                ))}
              </div>
            </SectionShell>

            {/* s2 */}
            <SectionShell id="s2" num="2" title="Datos que recogemos">
              <p className="mb-5">
                Tratamos datos en dos contextos: el{" "}
                <strong className="text-ink">administrador</strong> (quien contrata el servicio) y los{" "}
                <strong className="text-ink">miembros de la congregación</strong> que él introduce en la plataforma.
              </p>
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5 sm:p-6">
                  <h3 className="mb-3 font-bold text-violet-fuerte">2.1 Administrador</h3>
                  <BulletList items={[
                    "Nombre completo y correo electrónico",
                    "Nombre de la iglesia o entidad religiosa",
                    "Datos de facturación para gestión de suscripción",
                    "Datos de pago procesados por Stripe (no almacenamos tarjetas)",
                    "Dirección IP y datos técnicos de acceso",
                    "Historial de uso de la plataforma",
                  ]} />
                </div>
                <div className="rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5 sm:p-6">
                  <h3 className="mb-3 font-bold text-violet-fuerte">2.2 Miembros de la congregación</h3>
                  <p className="mb-3 text-[14px] leading-relaxed">
                    El administrador introduce estos datos. EkklesiaOnline actúa como{" "}
                    <strong>encargado del tratamiento</strong>; la iglesia es la responsable.
                  </p>
                  <BulletList items={[
                    "Nombre completo y datos de contacto",
                    "Fecha de nacimiento y estado civil",
                    "Bautismo, membresía y ministerio",
                    "Asistencia y participación en actividades",
                    "Información financiera (diezmos, ofrendas)",
                    "Fotografía de perfil (opcional)",
                  ]} />
                </div>
              </div>
              <div className="mt-5 rounded-xl border border-red-200 bg-[#fff5f5] px-5 py-4 text-[13.5px] text-[#7f1d1d]">
                <strong className="text-[#b91c1c]">Datos de categoría especial:</strong> La información sobre creencias
                religiosas constituye una categoría especial conforme al art. 9 RGPD. Ver apartado 4.
              </div>
            </SectionShell>

            {/* s3 */}
            <SectionShell id="s3" num="3" title="Finalidad y base legal del tratamiento">
              <p className="mb-4">
                Tratamos tus datos únicamente para las finalidades descritas, con la base legal correspondiente:
              </p>
              <div className="overflow-x-auto rounded-xl border border-violet-light/80">
                <table className="w-full min-w-[540px] border-collapse text-[13.5px]">
                  <thead>
                    <tr className="bg-violet-fuerte text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Finalidad</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Base legal</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Aplicable a</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purposeRows.map((row) => (
                      <tr key={row.purpose} className="border-b border-violet-light/60 last:border-b-0 hover:bg-violet-light/30">
                        <td className="px-4 py-3 align-top font-medium text-violet-oscuro">{row.purpose}</td>
                        <td className="px-4 py-3 align-top"><LegalBadge kind={row.legal}>{row.badgeText}</LegalBadge></td>
                        <td className="px-4 py-3 align-top" style={{ color: textBody }}>{row.who}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <InfoBox accent>
                <strong className="text-[#7a5800]">Importante:</strong> Nunca usamos datos de miembros para fines distintos
                al servicio contratado. No realizamos perfiles comerciales ni cedemos datos a terceros con fines de marketing.
              </InfoBox>
            </SectionShell>

            {/* s4 */}
            <SectionShell id="s4" num="4" title="Datos especialmente sensibles">
              <p className="mb-4">
                La información sobre creencias religiosas constituye una{" "}
                <strong>categoría especial de datos</strong> conforme al art. 9 RGPD. La tratamos exclusivamente porque:
              </p>
              <BulletList items={[
                "El tratamiento lo realiza la propia entidad religiosa en el ejercicio legítimo de su actividad (art. 9.2.d RGPD).",
                "Los datos se refieren a personas con relación directa con la iglesia.",
                "No se comunican fuera de la entidad sin consentimiento expreso del interesado.",
              ]} />
              <InfoBox>
                <strong className="text-violet-fuerte">Para administradores de iglesias:</strong> Son responsables de
                informar a su congregación sobre el uso de EkklesiaOnline. Facilitamos plantillas de información bajo
                petición.
              </InfoBox>
            </SectionShell>

            {/* s5 */}
            <SectionShell id="s5" num="5" title="Conservación de datos">
              <p className="mb-4">Los datos se conservan durante el tiempo estrictamente necesario:</p>
              <div className="overflow-x-auto rounded-xl border border-violet-light/80">
                <table className="w-full min-w-[440px] border-collapse text-[13.5px]">
                  <thead>
                    <tr className="bg-violet-fuerte text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Tipo de dato</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Período</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conservationRows.map((row) => (
                      <tr key={row.tipo} className="border-b border-violet-light/60 last:border-b-0 hover:bg-violet-light/30">
                        <td className="px-4 py-3 align-top font-medium text-violet-oscuro">{row.tipo}</td>
                        <td className="px-4 py-3 align-top" style={{ color: textBody }}>{row.periodo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Tras la cancelación, dispones de <strong>30 días</strong> para exportar tus datos. Después se eliminan
                permanentemente, salvo los que deban conservarse por obligación legal.
              </p>
            </SectionShell>

            {/* s6 */}
            <SectionShell id="s6" num="6" title="Encargados del tratamiento y terceros">
              <p className="mb-5">
                Para prestar el servicio trabajamos con proveedores que actúan como encargados bajo acuerdos conformes al
                RGPD:
              </p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  { name: "MongoDB Atlas", desc: "Almacenamiento de datos. Infraestructura cloud con centros en la UE.", link: "https://www.mongodb.com/legal/privacy-policy" },
                  { name: "Stripe", desc: "Pagos y suscripciones (PCI-DSS). No almacenamos datos de tarjeta.", link: "https://stripe.com/es/privacy" },
                  { name: "Resend", desc: "Correos transaccionales (notificaciones, facturas, alertas).", link: "https://resend.com/legal/privacy-policy" },
                  { name: "Vercel / FastAPI", desc: "Alojamiento del frontend y del backend de la plataforma.", link: null },
                ].map((p) => (
                  <div key={p.name} className="flex flex-col rounded-xl border border-violet-light/80 p-4" style={{ background: lavender }}>
                    <p className="font-bold text-violet-oscuro">{p.name}</p>
                    <p className="mt-2 flex-1 text-[13px] leading-relaxed" style={{ color: textBody }}>{p.desc}</p>
                    {p.link && (
                      <a href={p.link} target="_blank" rel="noopener noreferrer" className="mt-3 text-[12.5px] font-semibold text-violet-fuerte hover:underline">
                        Ver política →
                      </a>
                    )}
                  </div>
                ))}
              </div>
              <InfoBox accent>
                <strong className="text-[#7a5800]">No cedemos datos a terceros</strong> con fines comerciales. Todos los
                proveedores acceden únicamente a los datos imprescindibles para su función.
              </InfoBox>
            </SectionShell>

            {/* s7 */}
            <SectionShell id="s7" num="7" title="Transferencias internacionales de datos">
              <p className="mb-4">
                Algunos proveedores tienen sede fuera del EEE. En esos casos garantizamos las transferencias con:
              </p>
              <BulletList items={[
                "Stripe (EE.UU.): Cláusulas Contractuales Tipo y marco UE-EE.UU.",
                "MongoDB Atlas: Datos europeos en regiones UE; otras regiones con Cláusulas Contractuales Tipo.",
                "Resend: Servicio en EE.UU. con Cláusulas Contractuales Tipo aplicables.",
              ]} />
              <InfoBox>
                <strong className="text-violet-fuerte">Tu control:</strong> Puedes solicitar información sobre las garantías
                específicas contactándonos en <strong>privacidad@ekklesiaonline.app</strong>.
              </InfoBox>
            </SectionShell>

            {/* s8 */}
            <SectionShell id="s8" num="8" title="Tus derechos">
              <p className="mb-4">El RGPD y la LOPDGDD te reconocen los siguientes derechos:</p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {rightsCards.map((r) => (
                  <div
                    key={r.title}
                    className="flex gap-3 rounded-xl border border-[#e4dffe] px-4 py-4 transition-shadow hover:shadow-md"
                    style={{ background: lavender }}
                  >
                    <span className="mt-0.5 text-lg leading-none" aria-hidden>{r.icon}</span>
                    <div>
                      <p className="text-[13px] font-bold text-violet-oscuro">{r.title}</p>
                      <p className="mt-1 text-[13px] leading-snug text-[#6b6585]">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[14px]">
                Para ejercer estos derechos, envía tu solicitud a{" "}
                <strong>privacidad@ekklesiaonline.app</strong> con tu nombre, el derecho que deseas ejercer y copia de tu
                documento de identidad. Respondemos en un máximo de <strong>30 días hábiles</strong>.
              </p>
            </SectionShell>

            {/* s9 */}
            <SectionShell id="s9" num="9" title="Seguridad de los datos">
              <p className="mb-4">
                Aplicamos medidas técnicas y organizativas para proteger los datos frente a accesos no autorizados:
              </p>
              <BulletList items={[
                <>Comunicaciones cifradas mediante <strong>HTTPS / TLS 1.3</strong> en todas las conexiones.</>,
                <>Autenticación mediante <strong>tokens JWT</strong> con expiración controlada.</>,
                <>Contraseñas almacenadas con <strong>hashing seguro</strong> (bcrypt).</>,
                "Acceso restringido a personal autorizado bajo principio de mínimo privilegio.",
                "Copias de seguridad cifradas con retención máxima de 90 días.",
                "Monitorización activa de accesos y actividad anómala.",
              ]} />
              <InfoBox>
                <strong className="text-violet-fuerte">En caso de brecha:</strong> Si se produce una violación que pueda
                suponer un riesgo para tus derechos, te notificaremos sin dilación e informaremos a la AEPD en el plazo
                máximo de 72 horas (art. 33 RGPD).
              </InfoBox>
            </SectionShell>

            {/* s10 */}
            <SectionShell id="s10" num="10" title="Menores de edad">
              <p className="mb-3.5">
                EkklesiaOnline no está dirigido a menores de 14 años como usuarios directos. Reconocemos que las iglesias
                gestionan información de familias completas, incluyendo niños y jóvenes.
              </p>
              <p className="mb-3.5">
                Cuando los administradores introduzcan datos de menores, son responsables de haber obtenido el
                consentimiento de sus padres o tutores legales.
              </p>
              <p>
                Aplicamos protecciones adicionales a los perfiles identificados como menores dentro de la plataforma.
              </p>
            </SectionShell>

            {/* s11 */}
            <SectionShell id="s11" num="11" title="Cambios en esta política">
              <p className="mb-3.5">
                Podemos actualizar esta política para reflejar cambios en el servicio, la legislación o nuestras prácticas.
              </p>
              <p className="mb-3.5">
                Cuando los cambios sean significativos, notificaremos con al menos <strong>15 días de antelación</strong>{" "}
                por correo y con un aviso dentro de la plataforma.
              </p>
              <p>
                La versión actualizada estará siempre disponible en{" "}
                <Link to="/privacidad" className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro">
                  esta página
                </Link>
                . El uso continuado del servicio implica la aceptación de los cambios.
              </p>
            </SectionShell>

            {/* s12 */}
            <SectionShell id="s12" num="12" title="Contacto">
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <h3 className="font-display text-base font-bold text-ink">¿Preguntas sobre privacidad?</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
                    Contacta con el responsable del tratamiento para cualquier duda sobre esta política o el tratamiento de
                    tus datos personales.
                  </p>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink-2">
                    <strong className="text-ink">Responsable:</strong> Osmel Enamorado
                    <br />
                    <strong className="text-ink">Servicio:</strong> EkklesiaOnline
                    <br />
                    <strong className="text-ink">Web:</strong> ekklesiaonline.app
                  </p>
                  <p className="mt-4 text-[13px] leading-relaxed text-ink-3">
                    Respondemos en un máximo de 30 días hábiles. Si no quedas satisfecho, puedes acudir a la{" "}
                    <strong className="text-ink-2">AEPD</strong> en{" "}
                    <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-fuerte hover:underline">
                      www.aepd.es
                    </a>
                    .
                  </p>
                </div>
                <aside className="flex flex-col justify-center rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5 sm:p-6 lg:col-span-5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gold">Correo de privacidad</p>
                  <a
                    href="mailto:privacidad@ekklesiaonline.app"
                    className="mt-3 break-all font-display text-base font-semibold text-violet-fuerte underline decoration-violet-fuerte/25 underline-offset-4 transition-colors hover:text-violet-oscuro"
                  >
                    privacidad@ekklesiaonline.app
                  </a>
                  <p className="mt-4 text-[13px] leading-relaxed text-ink-3">
                    Indica tu nombre, el derecho que ejercitas y adjunta copia de tu DNI cuando proceda.
                  </p>
                </aside>
              </div>
            </SectionShell>

            {/* Sentinel: cuando entra en viewport, el TOC fijo se oculta */}
            <div ref={sentinelRef} aria-hidden />
          </div>

        </div>{/* fin del wrapper lg:pr */}
      </main>

        <LegalTocFixed
          sentinelRef={sentinelRef}
          ariaLabel="Índice de la política de privacidad"
          tocItems={tocItems}
        />

      <Footer />
    </div>
  );
}
